import { formatUnitCost } from 'src/pages/authenticated/procurement/replenishment/components/unitCostFormat';
import { ReceiveCostLabel } from '../ReceiveCostLabel';
import { T } from 'gt-react';
import { formatCurrency } from 'src/pages/authenticated/sales-orders/components/line-item-table/priceUtils';
import type { DraftPurchaseOrderDetail } from '@erp/rpc';

const fmtDate = (iso: string | null): string => (iso ? iso.slice(0, 10) : '—');

/**
 * Shared read-only renderer for a replenishment draft PO — the header facts an
 * approver decides from (supplier, ship-to, total, who placed it, when) plus
 * the full line detail. Rendered by both PO detail surfaces: the inbox
 * approval pane and the orders-board drill-in
 * (docs/procurement/po-approval-workflow.md §7). Callers own loading/error
 * states; this component is pure display.
 */
export function DraftPurchaseOrderDetails({
  detail,
}: {
  detail: DraftPurchaseOrderDetail;
}) {
  return (
    <div className="flex flex-col gap-3">
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
        <FactCell
          label={<T context="draft PO detail fact label">Supplier</T>}
          value={detail.supplierName}
        />
        <FactCell
          label={<T context="draft PO detail fact label">Ship to</T>}
          value={detail.shipToWarehouseName}
        />
        <FactCell
          label={<T context="draft PO detail fact label">Total</T>}
          value={
            detail.totalAmount != null
              ? formatCurrency(detail.totalAmount)
              : null
          }
        />
        <FactCell
          label={<T context="draft PO detail fact label">Created by</T>}
          value={detail.createdByUserName}
        />
        <FactCell
          label={<T context="draft PO detail fact label">Created</T>}
          value={fmtDate(detail.createdAt)}
        />
        <FactCell
          label={<T context="draft PO detail fact label">Order date</T>}
          value={fmtDate(detail.orderDate)}
        />
      </dl>

      <div className="overflow-hidden rounded-md border">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b bg-muted/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
              <th className="px-2.5 py-1.5 font-medium">
                <T context="draft PO lines column">#</T>
              </th>
              <th className="px-2.5 py-1.5 font-medium">
                <T context="draft PO lines item description column">
                  Item Description
                </T>
              </th>
              <th className="px-2.5 py-1.5 text-right font-medium">
                <T context="draft PO lines column">Qty</T>
              </th>
              <th className="px-2.5 py-1.5 font-medium">
                <T context="draft PO lines column">UoM</T>
              </th>
              <th className="px-2.5 py-1.5 text-right font-medium">
                <T context="draft PO lines column">Unit price</T>
              </th>
              <th className="px-2.5 py-1.5 text-right font-medium">
                <T context="draft PO lines column">Line total</T>
              </th>
              <th className="px-2.5 py-1.5 font-medium">
                <T context="draft PO lines column: receiving valuation basis and amount">
                  Receive cost
                </T>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {detail.lines.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-2.5 py-3 text-center text-muted-foreground"
                >
                  <T context="empty draft PO lines table">
                    No line items on this purchase order.
                  </T>
                </td>
              </tr>
            ) : (
              detail.lines.map((line, index) => (
                <tr key={line.id}>
                  <td className="px-2.5 py-1.5 text-muted-foreground">
                    {line.lineNumber ?? index + 1}
                  </td>
                  <td className="px-2.5 py-1.5">
                    <div className="font-medium text-foreground">
                      {line.productName ?? line.description ?? '—'}
                    </div>
                    {(line.productSku ||
                      (line.productName && line.description)) && (
                      <div className="text-[11px] text-muted-foreground">
                        {[line.productSku, line.productName && line.description]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    )}
                  </td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">
                    {line.quantity.toLocaleString()}
                  </td>
                  <td className="px-2.5 py-1.5 text-muted-foreground">
                    {line.unitOfMeasure ?? '—'}
                  </td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">
                    {line.unitPrice != null
                      ? formatUnitCost(line.unitPrice)
                      : '—'}
                  </td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">
                    {line.totalPrice != null
                      ? formatCurrency(line.totalPrice)
                      : '—'}
                  </td>
                  <td className="px-2.5 py-1.5">
                    <ReceiveCostLabel
                      basis={line.ppvReceiveAt}
                      cost={line.costConstraintUnitCost}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FactCell({
  label,
  value,
}: {
  label: React.ReactNode;
  value: string | null;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="truncate text-[13px] text-foreground" title={value ?? ''}>
        {value ?? '—'}
      </dd>
    </div>
  );
}
